import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ExternalLink, Copy, Trash2, MessageCircle, Image as ImageIcon, Pencil } from "lucide-react";
import { formatDateDE, toYMDLocal } from "@/lib/utils";
import { CustomerPicker } from "@/components/CustomerPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OWN_PHONE = "4915127282560";
const EMPTY = {
  anrede: "", anlass: "Kulinarischer Dinner-Abend", untertitel: "",
  event_date: toYMDLocal(), event_time: "18:30", ort: "", gaeste: 0,
  gruss: "", menuekarte_id: "", slug: "",
};

export default function EventSeitenTab({ pwd }) {
  const [form, setForm] = useState(EMPTY);
  const [menus, setMenus] = useState([]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(null);
  const [wuensche, setWuensche] = useState({});
  const [editId, setEditId] = useState(null);
  const [phones, setPhones] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const headers = { "X-Admin-Password": pwd };

  const load = useCallback(async () => {
    try {
      const [ev, mk] = await Promise.all([
        axios.get(`${API}/admin/event-seiten`, { headers }),
        axios.get(`${API}/admin/menuekarten`, { headers }),
      ]);
      setList(ev.data || []);
      setMenus(mk.data || []);
    } catch {
      toast.error("Event-Seiten konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwd]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.anrede.trim()) { toast.error("Bitte die Anrede eintragen, z. B. „Frau Koch\u201c"); return; }
    if (!editId && !form.menuekarte_id) { toast.error("Bitte eine Menükarte wählen"); return; }
    setSaving(true);
    try {
      const payload = { ...form, gaeste: Number(form.gaeste) || 0 };
      if (editId) {
        await axios.put(`${API}/admin/event-seiten/${editId}`, payload, { headers });
        toast.success("Änderungen gespeichert — die Seite ist sofort aktualisiert", { duration: 5000 });
        setEditId(null);
        setForm(EMPTY);
        await load();
      } else {
        const { data } = await axios.post(`${API}/admin/event-seiten`, payload, { headers });
        toast.success("Event-Seite angelegt — Link ist sofort erreichbar", { duration: 6000 });
        setForm(EMPTY);
        await load();
        window.open(data.url, "_blank", "noopener");
      }
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (e) => {
    setEditId(e.id);
    setForm({
      anrede: e.anrede || "", anlass: e.anlass || "", untertitel: e.untertitel || "",
      event_date: e.event_date || toYMDLocal(), event_time: e.event_time || "",
      ort: e.ort || "", gaeste: e.gaeste || 0, gruss: e.gruss || "",
      menuekarte_id: e.menuekarte_id || "", slug: e.slug || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditId(null); setForm(EMPTY); };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link kopiert");
    } catch {
      window.prompt("Link kopieren:", url);
    }
  };

  const nachricht = (e) =>
    `*Ihr Abend am ${formatDateDE(e.event_date)}*\n\n`
    + `Guten Tag${e.anrede ? ` ${e.anrede}` : ""}, ich freue mich schon sehr auf unseren Abend.\n`
    + `Hier finden Sie alles auf einer Seite: Menü, Ablauf des Abends und einen kleinen Countdown.\n\n`
    + `${e.url}\n\n`
    + `Den Link können Sie gerne auch an Ihre Gäste weitergeben.\n`
    + `Kulinarische Grüße, Lukas Stutz`;

  const whatsapp = (e, nummer = "") => {
    const ziel = String(nummer || "").replace(/[^\d]/g, "").replace(/^0049/, "49").replace(/^0/, "49");
    window.open(`https://wa.me/${ziel}?text=${encodeURIComponent(nachricht(e))}`, "_blank", "noopener");
  };

  const sendMitBild = async (e) => {
    try {
      const res = await fetch(e.einladung_url);
      const blob = await res.blob();
      const file = new File([blob], "Einladung.jpg", { type: "image/jpeg" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: nachricht(e) });
        return;
      }
      window.open(e.einladung_url, "_blank", "noopener");
      await navigator.clipboard.writeText(nachricht(e));
      toast.success("Bild geöffnet — Text ist kopiert. Bild in WhatsApp anhängen und Text einfügen.",
        { duration: 8000 });
    } catch (err) {
      toast.error("Teilen hat nicht funktioniert — bitte „Einladungsbild“ und „Senden“ einzeln nutzen.");
    }
  };

  const toggleWuensche = async (e) => {
    if (open === e.id) { setOpen(null); return; }
    setOpen(e.id);
    try {
      const { data } = await axios.get(`${API}/admin/event-seiten/${e.id}/wuensche`, { headers });
      setWuensche((w) => ({ ...w, [e.id]: data || [] }));
    } catch {
      toast.error("Wünsche konnten nicht geladen werden");
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Event-Seite für ${e.anrede || e.anlass} wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/event-seiten/${e.id}`, { headers });
      toast.success("Gelöscht");
      load();
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    }
  };

  const IN = "mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-3 text-sm";
  const LB = "text-[10px] uppercase tracking-[0.2em] text-[#757067]";

  return (
    <div data-testid="event-seiten-tab" className="space-y-10">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Event-Seite für Gäste</p>
        <p className="text-sm text-[#757067] mt-1 max-w-2xl">
          Eine persönliche Seite mit Begrüßung, laufendem Countdown, dem Menü, dem Ablauf des Abends
          und einer kurzen Vorstellung. Du schickst den Link dem Gastgeber — er kann ihn selbst per
          WhatsApp an alle Gäste weitergeben.
        </p>
      </div>

      <div className={`border p-5 md:p-6 ${editId ? "border-[#BA6D52] bg-[#FDF5EF]" : "border-[#E3DECE] bg-[#FAFAF7]"}`}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">
            {editId ? "Event-Seite bearbeiten" : "Neue Event-Seite"}
          </p>
          {editId && (
            <button onClick={cancelEdit} data-testid="event-edit-cancel"
              className="text-[10px] uppercase tracking-[0.15em] text-[#757067] hover:text-[#BA6D52]">
              Abbrechen
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={LB}>Anrede der Gäste *</label>
            <input value={form.anrede} onChange={(e) => set("anrede", e.target.value)}
              data-testid="event-anrede-input" placeholder="z. B. Frau Koch / Familie Burkel" className={IN} />
          </div>
          <div>
            <label className={LB}>Kunde aus der Kartei (optional)</label>
            <div className="mt-1">
              <CustomerPicker pwd={pwd} value={form.ort} onChange={(v) => set("ort", v)}
                onSelect={(k) => setForm((f) => ({
                  ...f,
                  anrede: f.anrede || k.ansprechpartner || k.betrieb,
                  ort: k.ort || f.ort,
                }))}
                placeholder="Ort/Adresse suchen oder tippen" testId="event-kunde" />
            </div>
          </div>
          <div>
            <label className={LB}>Anlass</label>
            <input value={form.anlass} onChange={(e) => set("anlass", e.target.value)}
              data-testid="event-anlass" className={IN} />
          </div>
          <div>
            <label className={LB}>Untertitel (optional)</label>
            <input value={form.untertitel} onChange={(e) => set("untertitel", e.target.value)}
              data-testid="event-untertitel" placeholder="z. B. Ein 5-Gang-Menü" className={IN} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className={LB}>Datum *</label>
              <input type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)}
                data-testid="event-date" className={IN} />
            </div>
            <div>
              <label className={LB}>Uhrzeit</label>
              <input type="time" value={form.event_time} onChange={(e) => set("event_time", e.target.value)}
                data-testid="event-time" className={IN} />
            </div>
          </div>
          <div>
            <label className={LB}>Anzahl Gäste (optional)</label>
            <input type="number" min="0" value={form.gaeste} onChange={(e) => set("gaeste", e.target.value)}
              data-testid="event-gaeste" className={IN} />
          </div>
          <div className="md:col-span-2">
            <label className={LB}>Menükarte *</label>
            <select value={form.menuekarte_id} onChange={(e) => set("menuekarte_id", e.target.value)}
              data-testid="event-menu-select" className={IN}>
              <option value="">— Menükarte wählen —</option>
              {menus.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}{m.gaenge?.length ? ` (${m.gaenge.length} Gänge)` : ""}
                </option>
              ))}
            </select>
            {menus.length === 0 && (
              <p className="text-xs text-[#BA6D52] mt-2">
                Noch keine Menükarte gespeichert — leg sie unter „Mehr → Menükarten" an.
              </p>
            )}
            {editId && (
              <p className="text-xs text-[#757067] mt-2">
                Leer lassen = das Menü bleibt unverändert. Wählst du eine andere Karte, wird das Menü ersetzt.
              </p>
            )}
          </div>
          <div className="md:col-span-2">
            <label className={LB}>Eigene Link-Adresse (optional)</label>
            <div className="mt-1 flex items-center border border-[#E3DECE] bg-white focus-within:border-[#BA6D52]">
              <span className="text-xs text-[#9A948A] pl-3 py-3 whitespace-nowrap">stutz-cuisine.com/abend/</span>
              <input value={form.slug} onChange={(e) => set("slug", e.target.value)}
                data-testid="event-slug"
                placeholder="firmenfeier-streetec"
                className="flex-1 min-w-0 bg-transparent focus:outline-none px-2 py-3 text-sm" />
            </div>
            <p className="text-xs text-[#757067] mt-2">
              {editId
                ? "Du kannst die Adresse ändern — der alte Link funktioniert weiterhin."
                : "Leer lassen? Dann wird die Adresse automatisch aus Anrede und Datum gebildet."}
            </p>
          </div>
          <div className="md:col-span-2">
            <label className={LB}>Persönlicher Gruß (optional)</label>
            <textarea rows={3} value={form.gruss} onChange={(e) => set("gruss", e.target.value)}
              data-testid="event-gruss"
              placeholder="Bleibt leer? Dann steht automatisch ein passender Satz zum Anlass dort."
              className={IN} />
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <button onClick={save} disabled={saving} data-testid="event-save"
            className="bg-[#BA6D52] text-white hover:bg-[#A55C43] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />}
            {editId ? "Änderungen speichern" : "Seite erstellen"}
          </button>
        </div>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-3">Angelegte Seiten</p>
        {loading ? (
          <Loader2 className="animate-spin text-[#BA6D52]" size={20} />
        ) : list.length === 0 ? (
          <p className="text-sm text-[#757067]" data-testid="event-empty">Noch keine Event-Seite angelegt.</p>
        ) : (
          <div className="space-y-3">
            {list.map((e) => (
              <div key={e.id} data-testid={`event-row-${e.id}`}
                className="border border-[#E3DECE] bg-white p-4 flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-[240px]">
                  <p className="font-serif text-lg">{e.anrede || "—"}</p>
                  <p className="text-xs text-[#757067] mt-1">
                    {formatDateDE(e.event_date)}{e.event_time ? ` · ${e.event_time} Uhr` : ""} · {e.anlass}
                    {e.gaenge?.length ? ` · ${e.gaenge.length} Gänge` : ""}
                  </p>
                  <p className="text-[11px] text-[#BA6D52] mt-1 break-all">{e.url}</p>
                  <p className="text-[11px] text-[#757067] mt-1">{e.views || 0}× aufgerufen</p>
                  <button onClick={() => toggleWuensche(e)} data-testid={`event-wuensche-${e.id}`}
                    className="mt-2 text-[11px] uppercase tracking-[0.15em] text-[#BA6D52] hover:underline">
                    Wünsche & Allergien ({e.wuensche_count || 0})
                  </button>
                  {open === e.id && (
                    <div className="mt-3 space-y-2" data-testid={`event-wuensche-list-${e.id}`}>
                      {(wuensche[e.id] || []).length === 0 ? (
                        <p className="text-xs text-[#757067]">Noch keine Meldung eingegangen.</p>
                      ) : (
                        wuensche[e.id].map((w) => (
                          <div key={w.id} className="border-l-2 border-[#BA6D52] bg-[#FAFAF7] px-3 py-2">
                            <p className="text-xs font-semibold text-[#3A352E]">
                              {w.name || "Gast"}{w.personen ? ` · ${w.personen} Pers.` : ""}
                            </p>
                            <p className="text-xs text-[#575249] whitespace-pre-wrap mt-1">{w.text}</p>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-3 min-w-[300px]">
                  <div className="flex flex-wrap items-center gap-2">
                    <input value={phones[e.id] ?? ""} inputMode="tel"
                      onChange={(ev) => setPhones((p) => ({ ...p, [e.id]: ev.target.value }))}
                      placeholder="Nummer, z. B. 0151 2728256" data-testid={`event-phone-${e.id}`}
                      className="w-[190px] bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2 text-xs" />
                    <button onClick={() => whatsapp(e, phones[e.id])} data-testid={`event-wa-${e.id}`}
                      className="text-[10px] uppercase tracking-[0.15em] border border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-3 py-2 inline-flex items-center gap-2">
                      <MessageCircle size={13} /> Senden
                    </button>
                    <button onClick={() => sendMitBild(e)} data-testid={`event-wa-bild-${e.id}`}
                      className="text-[10px] uppercase tracking-[0.15em] bg-emerald-600 text-white hover:bg-emerald-700 px-3 py-2 inline-flex items-center gap-2">
                      <ImageIcon size={13} /> Bild + Text senden
                    </button>
                    <button onClick={() => whatsapp(e, OWN_PHONE)} data-testid={`event-wa-self-${e.id}`}
                      className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-3 py-2">
                      An mich zum Prüfen
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => startEdit(e)} data-testid={`event-edit-${e.id}`}
                      className="text-[10px] uppercase tracking-[0.15em] border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-3 py-2 inline-flex items-center gap-2">
                      <Pencil size={13} /> Bearbeiten
                    </button>
                    <button onClick={() => copy(e.url)} data-testid={`event-copy-${e.id}`}
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-3 py-2 inline-flex items-center gap-2">
                    <Copy size={13} /> Link
                  </button>
                  <button onClick={() => copy(e.share_url)} data-testid={`event-copy-preview-${e.id}`}
                    title="Dieser Link zeigt in WhatsApp die persönliche Einladungskarte als Vorschaubild"
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-3 py-2 inline-flex items-center gap-2">
                    <Copy size={13} /> Link mit Vorschaubild
                  </button>
                  <a href={e.einladung_url} target="_blank" rel="noreferrer" download
                    data-testid={`event-einladung-${e.id}`}
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#D9A06A] text-[#9C4F31] hover:bg-[#FDF5EE] px-3 py-2 inline-flex items-center gap-2">
                    <ImageIcon size={13} /> Einladungsbild
                  </a>
                  <a href={e.url} target="_blank" rel="noreferrer" data-testid={`event-open-${e.id}`}
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-3 py-2 inline-flex items-center gap-2">
                    <ExternalLink size={13} /> Ansehen
                  </a>
                  <button onClick={() => remove(e)} data-testid={`event-delete-${e.id}`}
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-red-50 hover:text-red-700 px-3 py-2">
                    <Trash2 size={13} />
                  </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
